import { useCallback, useEffect, useRef, useState } from 'react';

// usePersistedListState - a list page's filters, search, sort, page and tab, kept for the sign-in
// (V2-024; DESIGN.md FLT-09).
//
// Stored in sessionStorage under `wrap.list:<pageKey>:<name>`, so leaving the page and coming
// back (or the browser Back button) restores it, and sign-out clears it with every other
// `wrap.*` key (IMP-07, utils/wrapStorage.ts). Drop-in for useState: same tuple, same updater
// form. A value that cannot be read back (storage blocked, shape changed) falls back to the
// initial value.

const PREFIX = 'wrap.list:';

function read<T>(key: string, initial: T): T {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (raw == null) return initial;
    const parsed = JSON.parse(raw) as T;
    // Same top-level shape as the default, so a stale entry from an older build cannot break the page.
    if (initial !== null && typeof initial === 'object' && !Array.isArray(initial)) {
      return parsed && typeof parsed === 'object' ? ({ ...initial, ...parsed } as T) : initial;
    }
    return typeof parsed === typeof initial || initial == null ? parsed : initial;
  } catch {
    return initial;
  }
}

export function persistedListKey(pageKey: string, name: string): string {
  return `${PREFIX}${pageKey}:${name}`;
}

export function usePersistedListState<T>(
  pageKey: string,
  name: string,
  initial: T,
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const key = persistedListKey(pageKey, name);
  const [value, setValue] = useState<T>(() => read(key, initial));
  const keyRef = useRef(key);
  keyRef.current = key;

  useEffect(() => {
    try {
      window.sessionStorage.setItem(keyRef.current, JSON.stringify(value));
    } catch {
      // Storage unavailable: the state still works for this visit.
    }
  }, [value]);

  const set = useCallback<React.Dispatch<React.SetStateAction<T>>>((next) => setValue(next), []);
  return [value, set];
}

export default usePersistedListState;
